/**
 * Pure helper that applies a flat list of UI answers onto a branching fax
 * questionnaire tree, normalising each answer to `{ id, text }[]`.
 *
 * @module procedureQuestionaire/utils/updateQuestionTreeWithAnswers
 */

/**
 * Recursively applies flat `answers` onto a branching questionnaire tree.
 *
 * @param questionTree - Root or child question nodes.
 * @param answers - Flat id → answer selections from the UI.
 * @returns New tree with `answer` fields normalised to `{ id, text }[]`.
 */
export const updateQuestionTreeWithAnswers = (
  questionTree: any[],
  answers: { id: string; answer: any }[],
): any[] => {
  return questionTree.map((question) => {
    const answer = answers.find((a) => a.id === question.id);
    let updatedQuestion = { ...question };

    if (answer) {
      const answerValue = answer.answer;

      // Handle multi-correct questions (array of selected option IDs)
      if (
        question.question_type === "multi_correct" &&
        Array.isArray(answerValue)
      ) {
        // Map each selected option ID to its corresponding option object
        const selectedOptions = answerValue.map((optionId) => {
          const selectedOption = question.options?.find(
            (option: any) => option.id === optionId,
          );
          return selectedOption
            ? { id: selectedOption.id, text: selectedOption.text }
            : { id: optionId, text: optionId };
        });

        updatedQuestion.answer = selectedOptions;
      }
      // Handle single-correct and other question types
      else {
        const selectedOption = question.options?.find(
          (option: any) => option.id === answerValue,
        );

        if (selectedOption) {
          updatedQuestion.answer = [
            {
              id: selectedOption.id,
              text: selectedOption.text,
            },
          ];
        } else {
          updatedQuestion.answer = [
            {
              id: answerValue,
              text: answerValue,
            },
          ];
        }
      }
    }

    // Recursively update children if they exist
    if (question.children && Object.keys(question.children).length > 0) {
      const updatedChildren: any = {};
      for (const [key, childQuestions] of Object.entries(question.children)) {
        updatedChildren[key] = updateQuestionTreeWithAnswers(
          childQuestions as any[],
          answers,
        );
      }
      updatedQuestion.children = updatedChildren;
    }

    return updatedQuestion;
  });
};
